"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Check, ShoppingCart, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CustomersOutput } from "@/interfaces/customers";
import {
  orderDeliveryTypeTransalation,
  OrderOutput,
} from "@/interfaces/orders";
import { RejectOrderDialog } from "./RejectOrderDialog";
import { PreOrderOutput } from "@/interfaces/pre-orders";
import { numberToMoney } from "@/util/moneyConversion";
import { orderPaymentDetailsMethodTranslations } from "@/interfaces/payments";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { localDate } from "@/util/dates";
import { PreOrderService } from "@/services/PreOrderService";
import { formatCep, formatDddPhone, formatDocument } from "@/util/formatters";

interface OrderConfirmationStepProps {
  preOrder: PreOrderOutput;
  customer: CustomersOutput;
  selectedAddressIndex: number;
  onNext: (order: OrderOutput | null) => void;
  onBack: () => void;
  onReject: (reason?: string) => void;
}

export function OrderConfirmationStep({
  preOrder,
  customer,
  onNext,
  onBack,
  onReject,
  selectedAddressIndex,
}: OrderConfirmationStepProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const preOrderService = new PreOrderService();

  const formattedPhone =
    formatDddPhone(customer.ddd, customer.phoneNumber, customer.internationalCode) ||
    "Não informado";

  const handleAcceptOrder = async () => {
    setIsLoading(true);

    try {
      const response = await preOrderService.acceptPreOrder(preOrder.id, {
        expand: "offers",
      });

      onNext(response.data ?? null);
    } catch (error) {
      console.error("Erro ao aceitar pré-pedido:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectOrder = async (reason?: string) => {
    setIsLoading(true);
    setShowRejectDialog(false);

    try {
      await preOrderService.rejectPreOrder(preOrder.id, {
        expand: "offers",
      });
      onReject(reason);
    } catch (error) {
      console.error("Erro ao rejeitar pré-pedido:", error);
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b pb-3">
            <CardTitle className="text-base font-medium text-gray-700">
              Informações do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">Nome</span>
              <span className="font-medium text-base">{customer.name}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">
                Telefone
              </span>
              <span className="font-medium text-base">{formattedPhone}</span>
            </div>
            {customer.document && (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Documento
                </span>
                <span className="font-medium text-base">
                  {formatDocument(customer.document)}
                </span>
              </div>
            )}

            {preOrder.delivery.deliveryType === "DELIVERY" &&
              customer.addresses &&
              customer.addresses.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-xs text-gray-500 font-medium mb-2">
                    Endereço de Entrega
                  </h4>
                  <div className="p-3 border border-gray-100 rounded-md bg-gray-50">
                    {(() => {
                      const address = customer.addresses[selectedAddressIndex];
                      return (
                        <>
                          <p className="font-medium text-sm">
                            {address.line1}, {address.number}
                            {address.line2 && `, ${address.line2}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {address.neighborhood}, {address.city}
                          </p>
                          {address.zipCode && (
                            <p className="text-xs text-gray-500">
                              CEP: {formatCep(address.zipCode)}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b pb-3">
            <CardTitle className="text-base font-medium text-gray-700">
              Detalhes do Pedido
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Tipo de Entrega
                </span>
                <span className="font-medium text-base">
                  {
                    orderDeliveryTypeTransalation[
                      preOrder.delivery.deliveryType
                    ]
                  }
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Método de Pagamento
                </span>
                <span className="font-medium text-base">
                  {
                    orderPaymentDetailsMethodTranslations[
                      preOrder.paymentMethod
                    ]
                  }
                </span>
              </div>

              {preOrder.delivery?.deliveryAt && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 font-medium">
                    Data de Entrega
                  </span>
                  <span className="font-medium text-base">
                    {format(
                      localDate(preOrder.delivery!.deliveryAt!),
                      "dd/MM/yyyy - HH:mm:ss",
                      { locale: ptBR }
                    )}
                  </span>
                </div>
              )}

              {preOrder.delivery?.deliveryValue &&
                preOrder.delivery.deliveryValue > 0 && (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-medium">
                      Valor da Entrega
                    </span>
                    <span className="font-medium text-base">
                      {numberToMoney(preOrder.delivery.deliveryValue)}
                    </span>
                  </div>
                )}
            </div>

            {preOrder.observation && (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Observação
                </span>
                <span className="font-medium text-sm">
                  {preOrder.observation}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <ShoppingCart className="h-4 w-4 mr-2 text-gray-500" />
            Itens do Pedido
          </CardTitle>
          <Badge
            variant="outline"
            className="bg-transparent text-gray-600 border-gray-200"
          >
            {(preOrder.items || []).length}{" "}
            {(preOrder.items || []).length === 1 ? "item" : "itens"}
          </Badge>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            {(preOrder.items || []).map((item, index) => (
              <div
                key={index}
                className="p-4 border border-gray-100 rounded-md bg-gray-50"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-medium text-base">{item.name}</h3>
                      <Badge
                        variant="outline"
                        className="ml-2 text-blue-600 bg-transparent border-blue-200 text-xs"
                      >
                        {item.kind === "OFFER" ? "Oferta" : "Produto"}
                      </Badge>
                    </div>
                    {item.observation && (
                      <p className="text-xs text-gray-500 mt-1">
                        Obs: {item.observation}
                      </p>
                    )}

                    {/* Exibir sub-itens se existirem */}
                    {item.items &&
                      Array.isArray(item.items) &&
                      item.items.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs font-medium text-gray-600 mb-2">
                            Itens inclusos:
                          </p>
                          {item.items.map((subItem, idx) => (
                            <div
                              key={idx}
                              className="pl-4 border-l-2 border-gray-200"
                            >
                              <p className="font-medium text-sm">
                                {subItem.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Preço unitário:{" "}
                                {numberToMoney(subItem.unitPrice)} • Qtd:{" "}
                                {subItem.quantity}
                              </p>

                              {/* Exibir adicionais do sub-item */}
                              {subItem.additionals &&
                                subItem.additionals.length > 0 && (
                                  <div className="mt-1 ml-2">
                                    <p className="text-xs font-medium text-gray-500 mb-1">
                                      Adicionais:
                                    </p>
                                    {subItem.additionals.map(
                                      (additional, addIdx) => (
                                        <div
                                          key={addIdx}
                                          className="text-xs text-gray-400 ml-2"
                                        >
                                          • {additional.name}{" "}
                                          {additional.unitPrice > 0 &&
                                            `(+${numberToMoney(
                                              additional.unitPrice
                                            )})`}
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      )}

                    {/* Exibir adicionais diretos do item principal */}
                    {item.additionals &&
                      Array.isArray(item.additionals) &&
                      item.additionals.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center gap-1 mb-2">
                            <span className="text-sm font-semibold text-gray-800">
                              Adicionais ({item.additionals.length})
                            </span>
                          </div>
                          {item.additionals.map((additional, addIdx) => (
                            <div
                              key={addIdx}
                              className="bg-orange-50 border border-orange-100 p-3 rounded-lg"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-900 text-sm">
                                      {additional.name}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Qtd: {additional.quantity}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <span>
                                      Preço unitário:{" "}
                                      {numberToMoney(additional.unitPrice)}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right ml-3">
                                  <div className="font-bold text-orange-600 text-sm">
                                    {numberToMoney(
                                      additional.unitPrice * additional.quantity
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Total adicional
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="text-right">
                    <div className="flex flex-col items-end">
                      <Badge
                        variant="outline"
                        className="mb-1 text-blue-600 bg-transparent border-blue-200 text-xs"
                      >
                        {item.quantity}{" "}
                        {item.quantity === 1 ? "unidade" : "unidades"}
                      </Badge>
                      <p className="font-medium text-sm">
                        {item.quantity} x {numberToMoney(item.unitPrice)}
                      </p>
                      <p className="font-bold text-base">
                        {numberToMoney(item.quantity * item.unitPrice)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {preOrder.delivery?.deliveryValue && preOrder.delivery.deliveryValue > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-100 flex justify-between items-center">
              <span className="text-base font-medium">Entrega:</span>
              <span className="text-base font-medium">
                {numberToMoney(preOrder.delivery.deliveryValue)}
              </span>
            </div>
          )}
          {preOrder.tax && preOrder.tax > 0 && (
            <div className="mt-2 p-4 bg-gray-50 rounded-md border border-gray-100 flex justify-between items-center">
              <span className="text-base font-medium">Imposto:</span>
              <span className="text-base font-medium">
                {numberToMoney(preOrder.tax)}
              </span>
            </div>
          )}
          <div className="mt-2 p-4 bg-gray-50 rounded-md border border-gray-100 flex justify-between items-center">
            <span className="text-base font-medium">Total do Pedido:</span>
            <span className="text-xl font-bold">
              {numberToMoney(preOrder.totalAmount)}
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          className="flex-1"
          disabled={isLoading}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Button
          variant="destructive"
          onClick={() => setShowRejectDialog(true)}
          className="flex-1"
          disabled={isLoading}
        >
          <X className="w-4 h-4 mr-2" />
          Recusar
        </Button>
        <Button
          onClick={handleAcceptOrder}
          className="flex-1"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          {isLoading ? "Processando..." : "Aceitar Pedido"}
        </Button>
      </div>

      <RejectOrderDialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onReject={handleRejectOrder}
      />
    </div>
  );
}
